import { useEffect, useState } from 'react';

import { Badge, Button, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { dataSimples, moeda } from '@/comuns/formato';
import { TODOS_OS_REGISTROS } from '@/comuns/exportar';
import { dados } from '@/data/porta';
import type { CategoriaFinanceira, Movimentacao, Titulo } from '@/data/tipos';
import { mesCurto } from './Relatorios';

/* ------------------------------------------------ composição do período */

export interface LinhaDaComposicao {
    rotulo: string;
    valorCentavos: number;
    tom: 'entrada' | 'saida' | 'neutro';
}

export interface ComposicaoDoPeriodoModalProps {
    titulo: string;
    descricao: string;
    linhas: LinhaDaComposicao[];
    resultadoCentavos: number;
    onFechar: () => void;
    onVerLancamentos: () => void;
}

/** Composição do mês (competência) ou do dia (fluxo). */
export function ComposicaoDoPeriodoModal({ titulo, descricao, linhas, resultadoCentavos, onFechar, onVerLancamentos }: ComposicaoDoPeriodoModalProps) {
    const cor = (tom: LinhaDaComposicao['tom']) => (tom === 'entrada' ? 'var(--success-strong)' : tom === 'saida' ? 'var(--danger-strong)' : 'var(--text-primary)');
    return (
        <Modal
            open width={520} onClose={onFechar} title={titulo} description={descricao}
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Fechar</Button>
                    <Button iconLeft={<Icon name="list" size={15} />} onClick={onVerLancamentos}>Ver lançamentos</Button>
                </>
            )}
        >
            <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '11px 20px', fontSize: 13 }}>
                {linhas.map((l) => (
                    <div key={l.rotulo} style={{ display: 'contents' }}>
                        <dt style={{ color: 'var(--text-secondary)' }}>{l.rotulo}</dt>
                        <dd style={{ margin: 0, textAlign: 'right', fontWeight: 600, color: cor(l.tom), fontVariantNumeric: 'tabular-nums' }}>{moeda(l.valorCentavos)}</dd>
                    </div>
                ))}
                <dt style={{ paddingTop: 10, borderTop: '1px solid var(--border-subtle)', fontWeight: 600, color: 'var(--text-primary)' }}>Resultado</dt>
                <dd style={{ margin: 0, paddingTop: 10, borderTop: '1px solid var(--border-subtle)', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: resultadoCentavos < 0 ? 'var(--danger-strong)' : 'var(--text-primary)' }}>
                    {resultadoCentavos < 0 ? '− ' : ''}{moeda(Math.abs(resultadoCentavos))}
                </dd>
            </dl>
        </Modal>
    );
}

/* -------------------------------------------- lançamentos da categoria */

export interface LancamentosDaCategoriaModalProps {
    categoria: CategoriaFinanceira;
    /**
     * As categorias que somam nesta: ela e as filhas. Pelo id — pelo nome,
     * "Outros" de receita misturava os títulos de "Outros" de despesa.
     */
    idsDaCategoria: string[];
    /** `YYYY-MM`. */
    mes: string;
    onFechar: () => void;
    onAbrirTitulo: (id: string) => void;
}

/** Os títulos da categoria na competência do mês — o que compõe o valor dela. */
export function LancamentosDaCategoriaModal({ categoria, idsDaCategoria, mes, onFechar, onAbrirTitulo }: LancamentosDaCategoriaModalProps) {
    const [titulos, setTitulos] = useState<Titulo[] | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    /* Texto, não o array: um array novo a cada render reabriria a leitura. */
    const chaveDosIds = idsDaCategoria.join('|');

    useEffect(() => {
        let vivo = true;
        const ids = new Set(chaveDosIds.split('|'));
        /* Todos os títulos: a página de 1.000 cortava em silêncio o que viesse depois. */
        dados().financeiro({ aba: 'visao', mes, porPagina: TODOS_OS_REGISTROS })
            .then((carga) => {
                if (!vivo) return;
                setTitulos(carga.titulos.filter((t) => t.categoriaId !== undefined && ids.has(t.categoriaId)
                    && t.competencia.slice(0, 7) === mes && t.situacao !== 'cancelado'));
            })
            .catch((e: unknown) => { if (vivo) setErro(e instanceof Error ? e.message : 'Não foi possível ler os lançamentos.'); });
        return () => { vivo = false; };
    }, [chaveDosIds, mes]);

    const total = (titulos ?? []).reduce((s, t) => s + t.valorCentavos, 0);

    return (
        <Modal
            open width={580} onClose={onFechar} title={categoria.nome}
            description={`${categoria.natureza === 'receita' ? 'Receita' : 'Despesa'} · competência ${mesCurto(mes)}`}
            footer={<Button variant="secondary" onClick={onFechar}>Fechar</Button>}
        >
            {erro && <Alert tone="danger" title="Não foi possível ler os lançamentos">{erro}</Alert>}
            {!erro && titulos === null && <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)' }}>Carregando…</p>}
            {titulos && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '14px 16px', background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                        <span style={{ fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-secondary)' }}>Total em {mesCurto(mes)}</span>
                        <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-ui)', fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{moeda(total)}</span>
                    </div>
                    {titulos.length === 0 ? (
                        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Nenhum lançamento desta categoria no mês.</p>
                    ) : (
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                            {titulos.map((t) => (
                                <li key={t.id}>
                                    <button
                                        type="button" onClick={() => onAbrirTitulo(t.id)}
                                        style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '9px 0', border: 0, borderBottom: '1px solid var(--border-subtle)', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-body)' }}
                                    >
                                        <span style={{ width: 84, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{dataSimples(t.vencimento)}</span>
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                            <span style={{ display: 'block', fontWeight: 500, color: 'var(--text-primary)' }}>{t.descricao}</span>
                                            <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)' }}>{t.contraparte}</span>
                                        </span>
                                        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{moeda(t.valorCentavos)}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </Modal>
    );
}

/* ------------------------------------------------ lançamento do extrato */

export interface LancamentoDoExtratoModalProps {
    movimento: Movimentacao;
    onFechar: () => void;
    onAbrirTitulo: (id: string) => void;
}

/** Um pagamento do extrato: bruto, taxa, líquido e o dia em que o dinheiro entra. */
export function LancamentoDoExtratoModal({ movimento: m, onFechar, onAbrirTitulo }: LancamentoDoExtratoModalProps) {
    const entrada = m.tipo === 'receber';
    return (
        <Modal
            open width={520} onClose={onFechar} title={m.descricao} description={`${dataSimples(m.pagoEm)} · ${m.conta ?? 'sem conta'}`}
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Fechar</Button>
                    <Button iconLeft={<Icon name="receipt" size={15} />} onClick={() => onAbrirTitulo(m.tituloId)}>Ver título</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '14px 16px', background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                    <span style={{ fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-secondary)' }}>{entrada ? 'Entrou' : 'Saiu'}</span>
                    <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-ui)', fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: entrada ? 'var(--success-strong)' : 'var(--danger-strong)' }}>
                        {entrada ? '+' : '−'}{moeda(entrada ? m.liquidoCentavos : m.valorCentavos)}
                    </span>
                    <Badge tone={entrada ? 'success' : 'warning'} dot>{entrada ? 'Recebimento' : 'Pagamento'}</Badge>
                </div>
                <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px 20px', fontSize: 13 }}>
                    <dt style={{ color: 'var(--text-secondary)' }}>{entrada ? 'Paciente' : 'Fornecedor'}</dt>
                    <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500 }}>{m.contraparte}</dd>
                    <dt style={{ color: 'var(--text-secondary)' }}>Valor bruto</dt>
                    <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{moeda(m.valorCentavos)}</dd>
                    <dt style={{ color: 'var(--text-secondary)' }}>Taxa</dt>
                    <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{m.taxaCentavos ? moeda(m.taxaCentavos) : '—'}</dd>
                    <dt style={{ color: 'var(--text-secondary)' }}>Forma</dt>
                    <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500 }}>{m.metodo ?? '—'}</dd>
                    <dt style={{ color: 'var(--text-secondary)' }}>Conta</dt>
                    <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500 }}>{m.conta ?? '—'}</dd>
                    <dt style={{ color: 'var(--text-secondary)' }}>Liquida em</dt>
                    <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500 }}>{dataSimples(m.liquidadoEm ?? m.pagoEm)}</dd>
                </dl>
            </div>
        </Modal>
    );
}
